(() => {
  if (customElements.get('evilko-bingo-shop')) return;
  const escape = value => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  const shuffle = values => {
    const copy = [...values];
    for (let index = copy.length - 1; index > 0; index--) {
      const other = Math.floor(Math.random() * (index + 1));
      [copy[index], copy[other]] = [copy[other], copy[index]];
    }
    return copy;
  };
  const defaults = {
    heading: 'Fresh tees. Keep playing.', loading: 'Loading tees…', previous: 'Previous shirt', next: 'Next shirt', pause: 'Pause shirts', resume: 'Resume shirts', choose_option: 'Choose {option}', add: 'Add to cart', adding: 'Adding…', added: 'Added to cart', view_cart: 'View cart', view_cart_count: 'View cart ({count})', counter: 'Shirt {current} of {total}', cart_ready: 'Keep playing. Shop anytime.', sold_out: 'Sold out', unavailable: 'This combination is unavailable.', load_error: 'We couldn’t load shirts right now.', retry: 'Try again', cart_error: 'We couldn’t add this shirt. Please try again.', cart_uncertain: 'Your cart may have been updated. View your cart before trying again.', price_from: 'From {price}'
  };
  const colors = {
    white: '#ffffff', black: '#17151c', 'brown-savana': '#a68c72', 'brown-savanna': '#a68c72', chocolate: '#4b3027', 'chocolate-brown': '#4b3027', 'dark-chocolate': '#4b3027', 'dark-brown': '#4b3027', mint: '#a9d8bc', 'mint-green': '#a9d8bc', daisy: '#ffd83d', yellow: '#ffd83d', heliconia: '#db3e8c', 'light-blue': '#adcde5', navy: '#202b48', sage: '#a9b6a0', sand: '#d7c9ad', red: '#be2933', royal: '#2855b6', 'royal-blue': '#2855b6', blue: '#2855b6', green: '#467758', purple: '#6e4091', pink: '#e9a5bd', 'light-pink': '#e9a5bd', orange: '#ee7a35', grey: '#a9abad', gray: '#a9abad', 'sport-grey': '#a9abad', 'sport-gray': '#a9abad', 'heather-gray': '#a9abad', 'heather-grey': '#a9abad'
  };
  class BingoShop extends HTMLElement {
    connectedCallback() {
      if (this.started) {
        if (this.needsReload && !this.pending && !this.uncertain) { this.needsReload = false; this.show(Math.max(0, this.index)); }
        this.discover();
        this.startTimer();
        return;
      }
      const configNode = this.querySelector('[data-bingo-shop-config]');
      if (!configNode) return;
      try { this.config = JSON.parse(configNode.textContent); } catch { return; }
      this.started = true;
      this.copy = { ...defaults, ...this.config.copy };
      this.cache = new Map();
      this.pool = [];
      this.index = -1;
      this.revision = 0;
      this.readers = new Set();
      this.pending = false;
      this.uncertain = false;
      this.userPaused = false;
      this.cartCount = Number.isInteger(this.config.cartItemCount) && this.config.cartItemCount >= 0 ? this.config.cartItemCount : null;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.motionResumed = false;
      this.rootUrl = this.localUrl(this.config.rootUrl || window.Shopify?.routes?.root || '/');
      if (!this.rootUrl) this.rootUrl = new URL('/', location.origin);
      if (!this.rootUrl.pathname.endsWith('/')) this.rootUrl.pathname += '/';
      this.cartUrl = this.localUrl(this.config.cartUrl) || new URL('cart', this.rootUrl);
      this.build();
      this.bind();
      this.addPool(shuffle(this.config.seeds || []));
      this.show(0);
      this.discover();
      this.startTimer();
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      this.timer = null;
      this.needsReload = Boolean(this.loading);
      if (this.needsReload) this.cache?.clear();
      this.loading = false;
      this.revision = (this.revision || 0) + 1;
      this.discoveryRevision = (this.discoveryRevision || 0) + 1;
      this.readers?.forEach(controller => controller.abort());
    }
    t(key, values = {}) {
      let copy = this.copy[key] || defaults[key] || key;
      for (const [name, value] of Object.entries(values)) copy = copy.split(`{${name}}`).join(String(value));
      return copy;
    }
    localUrl(value) {
      if (!value) return null;
      try {
        const url = new URL(value, location.origin);
        return url.origin === location.origin && /^https?:$/.test(url.protocol) ? url : null;
      } catch { return null; }
    }
    preview(url) {
      const previewId = new URL(location.href).searchParams.get('preview_theme_id');
      if (previewId && /^\d+$/.test(previewId)) url.searchParams.set('preview_theme_id', previewId);
      return url;
    }
    productHandle(seed) {
      const url = this.localUrl(seed.url || '');
      if (seed.url && !url) return '';
      const match = url?.pathname.match(/\/products\/([^/]+)\/?$/);
      const handle = seed.handle || match?.[1] || '';
      return /^[a-z0-9][a-z0-9-]*$/i.test(handle) ? handle : '';
    }
    addPool(seeds) {
      const seen = new Set(this.pool.map(seed => seed.handle));
      for (const seed of seeds) {
        const handle = this.productHandle(seed);
        if (!handle || seen.has(handle) || this.pool.length >= 48) continue;
        seen.add(handle);
        this.pool.push({ handle });
      }
      this.updateControls();
    }
    async request(url, options = {}, mutation = false) {
      const controller = new AbortController();
      if (!mutation) this.readers.add(controller);
      const timeout = setTimeout(() => controller.abort(), mutation ? 20000 : 12000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal });
        const text = await response.text();
        return { ok: response.ok, text };
      } finally {
        clearTimeout(timeout);
        this.readers.delete(controller);
      }
    }
    async json(url, options = {}, mutation = false) {
      const controller = new AbortController();
      if (!mutation) this.readers.add(controller);
      const timeout = setTimeout(() => controller.abort(), mutation ? 20000 : 12000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal });
        let data;
        try { data = await response.json(); } catch { throw new Error('Invalid response'); }
        if (!response.ok) {
          const error = new Error(data.description || data.message || this.t('cart_error'));
          error.status = response.status;
          throw error;
        }
        return data;
      } finally {
        clearTimeout(timeout);
        this.readers.delete(controller);
      }
    }
    async discover() {
      const revision = this.discoveryRevision = (this.discoveryRevision || 0) + 1;
      const catalog = this.localUrl(this.config.catalogUrl);
      if (!catalog) return;
      const pageCount = Math.max(1, Math.ceil(Math.min(25000, Number(this.config.productCount) || 24) / 24));
      const pages = new Set();
      let found = false;
      for (let attempt = 0; attempt < Math.min(6, pageCount); attempt++) {
        if (attempt >= 3 && found) break;
        let page = 1 + Math.floor(Math.random() * pageCount);
        while (pages.has(page)) page = page % pageCount + 1;
        pages.add(page);
        const url = new URL(catalog);
        url.searchParams.set('section_id', 'evilko-random-shirt-cards');
        url.searchParams.set('page', page);
        this.preview(url);
        try {
          const response = await this.request(url);
          if (!response.ok) continue;
          const html = response.text;
          if (!this.isConnected || revision !== this.discoveryRevision) return;
          const document = new DOMParser().parseFromString(html, 'text/html');
          const block = document.querySelector('[data-en-random-page]');
          if (!block || Number(block.dataset.enCurrentPage) !== page) continue;
          const seeds = [...block.querySelectorAll('[data-en-product-link]')].map(link => ({ url: link.getAttribute('href') }));
          if (seeds.length) found = true;
          const wasEmpty = this.pool.length === 0;
          this.addPool(shuffle(seeds));
          if (wasEmpty && this.pool.length) this.show(0);
        } catch { if (!this.isConnected || revision !== this.discoveryRevision) return; }
      }
    }
    eligible(product) {
      const title = String(product?.title || '');
      const tags = Array.isArray(product?.tags) ? product.tags : String(product?.tags || '').split(',');
      return /(?:^|\W)(?:tees?|t-?shirts?)(?:\W|$)/i.test(title) && !/monthly/i.test(title) && product.handle !== 'evilko-monthly-t-shirt-box' && !tags.some(tag => String(tag).trim().toLowerCase() === 'prize') && !product.requires_selling_plan && !(product.selling_plan_groups?.length) && product.available !== false && Array.isArray(product.variants) && product.variants.some(variant => variant.available && /^\d+$/.test(String(variant.id)));
    }
    async product(handle) {
      if (!this.cache.has(handle)) {
        const url = this.preview(new URL(`products/${encodeURIComponent(handle)}.js`, this.rootUrl));
        const pending = this.json(url).then(product => this.eligible(product) ? product : null).catch(error => { if (this.cache.get(handle) === pending) this.cache.delete(handle); throw error; });
        this.cache.set(handle, pending);
      }
      return this.cache.get(handle);
    }
    async show(index, direction = 1) {
      if (this.pending || this.uncertain) return;
      const revision = ++this.revision;
      this.loading = true;
      this.updateControls();
      this.status(this.t('loading'));
      this.querySelector('[data-eb-shop-retry]').hidden = true;
      const attempts = Math.min(this.pool.length, 8);
      for (let attempt = 0; attempt < attempts; attempt++) {
        const current = (index + attempt * direction + this.pool.length * 10) % this.pool.length;
        try {
          const product = await this.product(this.pool[current].handle);
          if (revision !== this.revision || !this.isConnected) return;
          if (!product) continue;
          this.index = current;
          this.current = product;
          this.choices = this.options().map(option => {
            if (/size/i.test(option.name)) return '';
            if (/colou?r/i.test(option.name)) return shuffle(product.variants.filter(variant => variant.available))[0]?.options?.[option.index] || option.values[0] || '';
            return option.values.length === 1 ? option.values[0] : '';
          });
          this.loading = false;
          this.renderProduct();
          return;
        } catch { if (revision !== this.revision || !this.isConnected) return; }
      }
      if (revision !== this.revision) return;
      this.loading = false;
      this.status(this.t('load_error'), true);
      this.querySelector('[data-eb-shop-retry]').hidden = false;
      this.updateControls();
    }
    options() {
      return (this.current?.options || []).map((option, index) => ({
        name: typeof option === 'string' ? option : option.name,
        values: Array.isArray(option.values) ? option.values.map(String) : [...new Set(this.current.variants.map(variant => String(variant.options[index])))], index
      }));
    }
    selected() {
      if (!this.current || this.choices.some(value => !value)) return null;
      return this.current.variants.find(variant => variant.options.every((value, index) => String(value) === this.choices[index])) || null;
    }
    matching() {
      return this.current?.variants.filter(variant => variant.options.every((value, index) => !this.choices[index] || String(value) === this.choices[index])) || [];
    }
    price(value) {
      try { return new Intl.NumberFormat(this.config.locale || document.documentElement.lang || 'en', { style: 'currency', currency: this.config.currency || window.Shopify?.currency?.active || 'USD' }).format(Number(value) / 100); }
      catch { return (Number(value) / 100).toFixed(2); }
    }
    imageUrl(value) {
      try {
        const url = new URL(typeof value === 'object' ? value?.src || value?.url || '' : value || '', location.origin);
        if (!/^https?:$/.test(url.protocol) || (!value && url.origin === location.origin)) return '';
        url.searchParams.set('width', '800');
        return url.href;
      } catch { return ''; }
    }
    build() {
      this.classList.add('eb-shop');
      this.setAttribute('aria-label', this.t('heading'));
      this.innerHTML = `<div class="eb-shop-heading"><h3>${escape(this.t('heading'))}</h3><div class="eb-shop-controls"><button type="button" data-eb-shop-previous aria-label="${escape(this.t('previous'))}"><span aria-hidden="true">‹</span></button><span class="eb-shop-counter" data-eb-shop-counter></span><button type="button" data-eb-shop-next aria-label="${escape(this.t('next'))}"><span aria-hidden="true">›</span></button><button type="button" data-eb-shop-pause aria-label="${escape(this.t('pause'))}" title="${escape(this.t('pause'))}">Ⅱ</button></div></div><div class="eb-shop-product"><div class="eb-shop-photo"><img data-eb-shop-image alt="" width="800" height="800" loading="lazy" decoding="async" hidden></div><div class="eb-shop-detail"><div class="eb-shop-product-heading"><div><h4 data-eb-shop-title></h4><p class="eb-shop-subtitle" data-eb-shop-subtitle></p></div><p class="eb-shop-price" data-eb-shop-price></p></div><div class="eb-shop-options" data-eb-shop-options></div><button class="eb-shop-add" type="button" data-eb-shop-add disabled><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 7h14l1 14H4L5 7Z"/><path d="M8 9V6a4 4 0 0 1 8 0v3"/></svg><span data-eb-shop-add-label>${escape(this.t('add'))}</span></button></div></div><div class="eb-shop-cart-bar" data-eb-shop-cart-bar><p class="eb-shop-status" data-eb-shop-status role="status" aria-live="polite"></p><a class="eb-shop-cart" href="${escape(this.cartUrl.href)}"><span data-eb-shop-cart-label>${escape(this.t('view_cart'))}</span><span aria-hidden="true">→</span></a></div><button class="eb-shop-retry" data-eb-shop-retry type="button" hidden>${escape(this.t('retry'))}</button>`;
      this.updateCartLabel();
    }
    renderProduct() {
      const title = String(this.current.title || '');
      const suffix = title.match(/\s[—–-]\s((?:(?:men'?s|women'?s|unisex)\s+)?(?:tee|t-?shirt)s?)$/i);
      this.querySelector('[data-eb-shop-title]').textContent = suffix ? title.slice(0, suffix.index) : title;
      this.querySelector('[data-eb-shop-subtitle]').textContent = suffix?.[1] || this.current.type || '';
      this.querySelector('[data-eb-shop-options]').innerHTML = this.options().filter(option => !(option.name === 'Title' && option.values.length === 1 && option.values[0] === 'Default Title')).map(option => {
        if (/colou?r/i.test(option.name)) return `<fieldset class="eb-shop-color"><legend>${escape(option.name)} <span aria-hidden="true">·</span> <span data-eb-shop-color-name="${option.index}">${escape(this.choices[option.index])}</span></legend><div class="eb-shop-swatches">${option.values.map(value => {
          const key = value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
          const color = colors[key];
          const selected = this.choices[option.index] === value;
          return `<button type="button" class="eb-shop-swatch${color ? '' : ' eb-shop-swatch-text'}${selected ? ' is-selected' : ''}" data-eb-shop-swatch data-eb-shop-option="${option.index}" data-eb-shop-value="${escape(value)}" aria-label="${escape(option.name)}: ${escape(value)}" aria-pressed="${selected}" title="${escape(value)}">${color ? `<span style="background:${color}" aria-hidden="true"></span>` : escape(value)}</button>`;
        }).join('')}</div></fieldset>`;
        return `<label class="eb-shop-select"><span>${escape(option.name)}</span><select data-eb-shop-option="${option.index}" aria-label="${escape(option.name)}"><option value="">${escape(this.t('choose_option', { option: option.name }))}</option>${option.values.map(value => `<option value="${escape(value)}"${this.choices[option.index] === value ? ' selected' : ''}>${escape(value)}</option>`).join('')}</select></label>`;
      }).join('');
      this.status('');
      this.updateVariant();
    }
    updateVariant() {
      const variant = this.selected();
      const matches = this.matching();
      const available = matches.filter(item => item.available);
      const price = variant?.price ?? Math.min(...(available.length ? available : matches).map(item => Number(item.price)));
      this.querySelector('[data-eb-shop-price]').textContent = Number.isFinite(price) ? (variant ? this.price(price) : this.t('price_from', { price: this.price(price) })) : '';
      const colorIndex = this.options().findIndex(option => /colou?r/i.test(option.name));
      const imageVariant = variant?.featured_image ? variant : matches.find(item => item.featured_image) || this.current.variants.find(item => item.featured_image && (colorIndex < 0 || !this.choices[colorIndex] || item.options[colorIndex] === this.choices[colorIndex]));
      const image = this.querySelector('[data-eb-shop-image]');
      const imageUrl = this.imageUrl(imageVariant?.featured_image || this.current.featured_image || this.current.images?.[0]);
      if (imageUrl && image.getAttribute('src') !== imageUrl) image.src = imageUrl;
      image.alt = `${this.current.title}${colorIndex >= 0 && this.choices[colorIndex] ? ` — ${this.choices[colorIndex]}` : ''}`;
      image.hidden = !imageUrl;
      this.querySelectorAll('[data-eb-shop-swatch]').forEach(swatch => {
        const selected = this.choices[Number(swatch.dataset.ebShopOption)] === swatch.dataset.ebShopValue;
        swatch.classList.toggle('is-selected', selected);
        swatch.setAttribute('aria-pressed', String(selected));
      });
      this.querySelectorAll('[data-eb-shop-color-name]').forEach(label => { label.textContent = this.choices[Number(label.dataset.ebShopColorName)] || ''; });
      if (this.choices.every(Boolean) && !variant) this.status(this.t('unavailable'), true);
      else if (variant && !variant.available) this.status(this.t('sold_out'), true);
      this.updateControls();
    }
    status(message, error = false) {
      const element = this.querySelector('[data-eb-shop-status]');
      element.textContent = message || this.t('cart_ready');
      element.classList.toggle('is-error', error);
      const bar = this.querySelector('[data-eb-shop-cart-bar]');
      bar.classList.toggle('is-error', error);
      bar.classList.toggle('is-added', Boolean(message && message === this.t('added') && !error));
    }
    updateCartLabel() {
      this.querySelector('[data-eb-shop-cart-label]').textContent = this.cartCount === null ? this.t('view_cart') : this.t('view_cart_count', { count: this.cartCount });
    }
    paused() { return this.userPaused || (!this.motionResumed && this.motion.matches); }
    updateControls() {
      if (!this.started || !this.querySelector('[data-eb-shop-add]')) return;
      const variant = this.selected();
      const button = this.querySelector('[data-eb-shop-add]');
      button.disabled = this.loading || this.pending || this.uncertain || !variant?.available;
      this.querySelector('[data-eb-shop-add-label]').textContent = this.pending ? this.t('adding') : variant && !variant.available ? this.t('sold_out') : this.t('add');
      this.querySelectorAll('[data-eb-shop-option]').forEach(select => { select.disabled = this.pending || this.uncertain || this.loading; });
      this.querySelectorAll('[data-eb-shop-previous],[data-eb-shop-next]').forEach(button => { button.disabled = this.pending || this.uncertain || this.loading || this.pool.length < 2; });
      const pause = this.querySelector('[data-eb-shop-pause]');
      const label = this.t(this.paused() ? 'resume' : 'pause');
      pause.textContent = this.paused() ? '▶' : 'Ⅱ';
      pause.setAttribute('aria-label', label);
      pause.setAttribute('title', label);
      pause.setAttribute('aria-pressed', String(this.paused()));
      pause.disabled = this.pending || this.uncertain;
      const counter = this.querySelector('[data-eb-shop-counter]');
      counter.textContent = `${this.index < 0 ? 0 : this.index + 1} / ${this.pool.length}`;
      counter.setAttribute('aria-label', this.t('counter', { current: this.index < 0 ? 0 : this.index + 1, total: this.pool.length }));
    }
    pause() { this.userPaused = true; this.updateControls(); }
    bind() {
      this.addEventListener('pointerenter', () => { this.hovered = true; });
      this.addEventListener('pointerleave', () => { this.hovered = false; });
      this.addEventListener('focusin', () => { this.focused = true; });
      this.addEventListener('focusout', event => { this.focused = this.contains(event.relatedTarget); });
      this.addEventListener('change', event => {
        const select = event.target.closest('[data-eb-shop-option]');
        if (!select || select.tagName !== 'SELECT' || this.pending || this.uncertain || this.loading) return;
        this.pause();
        this.choices[Number(select.dataset.ebShopOption)] = select.value;
        this.status('');
        this.updateVariant();
      });
      this.addEventListener('click', event => {
        const swatch = event.target.closest('[data-eb-shop-swatch]');
        if (!swatch || this.pending || this.uncertain || this.loading) return;
        this.pause();
        this.choices[Number(swatch.dataset.ebShopOption)] = swatch.dataset.ebShopValue;
        this.status('');
        this.updateVariant();
      });
      this.querySelector('[data-eb-shop-pause]').addEventListener('click', () => {
        const paused = this.paused();
        this.userPaused = !paused;
        if (paused) this.motionResumed = true;
        this.updateControls();
      });
      this.querySelector('[data-eb-shop-previous]').addEventListener('click', () => { this.pause(); this.show(this.index - 1, -1); });
      this.querySelector('[data-eb-shop-next]').addEventListener('click', () => { this.pause(); this.show(this.index + 1); });
      this.querySelector('[data-eb-shop-retry]').addEventListener('click', () => { this.pause(); this.show(Math.max(0, this.index)); });
      this.querySelector('[data-eb-shop-add]').addEventListener('click', () => this.add());
    }
    startTimer() {
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        if (!this.isConnected || document.hidden || this.paused() || this.hovered || this.focused || this.pending || this.uncertain || this.loading || this.pool.length < 2) return;
        this.show((this.index + 1 + Math.floor(Math.random() * (this.pool.length - 1))) % this.pool.length);
      }, Math.max(5000, Number(this.config.interval) || 8000));
    }
    async add() {
      const variant = this.selected();
      if (this.pending || this.uncertain || this.loading || !variant?.available) return;
      this.pause();
      this.pending = true;
      this.updateControls();
      this.status(this.t('adding'));
      try {
        await this.json(new URL('cart/add.js', this.rootUrl), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }) }, true);
        this.status(this.t('added'));
        try {
          const cart = await this.json(new URL('cart.js', this.rootUrl));
          if (Number.isInteger(cart.item_count) && cart.item_count >= 0) {
            this.cartCount = cart.item_count;
            this.updateCartLabel();
            document.querySelectorAll('[data-en-cart-count]').forEach(badge => { badge.textContent = String(cart.item_count); });
            document.querySelectorAll('[data-en-cart-label]').forEach(link => {
              if (link.dataset.labelTemplate) link.setAttribute('aria-label', link.dataset.labelTemplate.split('__COUNT__').join(String(cart.item_count)));
            });
          }
        } catch {}
      } catch (error) {
        if ([400, 404, 422].includes(error.status)) this.status(error.message || this.t('cart_error'), true);
        else { this.uncertain = true; this.status(this.t('cart_uncertain'), true); }
      } finally {
        this.pending = false;
        this.updateControls();
      }
    }
  }
  customElements.define('evilko-bingo-shop', BingoShop);
})();

